import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, CircularProgress, Snackbar, Alert, MenuItem, Typography } from "@mui/material";
import { makeEmployeeTransaction } from "../../../services/Employee/EmployeeApi";
import { getBanks } from "../../../services/dashboard/banks";

// props:
// - open (bool)
// - employeeId (number|string)
// - onClose() 
// - onPaid(result) optional callback after success
const SalaryPayment = ({ open, employeeId, onClose, onPaid, banks = [] }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today,
    orginal_amount: "",
    bouns: "0",
    discount: "0",
    total: "",
    action_type: "payment",
    employee_id: employeeId ?? "",
    bank_id: "",
    note: "",
    file: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // جديد: حالة لقائمة الخزن وحالة تحميلها
  const [banksList, setBanksList] = useState(banks || []);
  const [loadingBanks, setLoadingBanks] = useState(false);

  useEffect(() => {
    // ensure employee_id and fixed action_type = "payment"
    setForm((f) => ({ ...f, employee_id: employeeId ?? "", action_type: "payment" }));
  }, [employeeId]);

  // compute total automatically when amounts change
  useEffect(() => {
    const o = parseFloat(form.orginal_amount) || 0;
    const b = parseFloat(form.bouns) || 0;
    const d = parseFloat(form.discount) || 0;
    const calc = o + b - d;
    // set total only if orginal provided
    setForm((f) => ({ ...f, total: f.orginal_amount ? String(Number.isFinite(calc) ? calc.toFixed(2) : calc) : "" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.orginal_amount, form.bouns, form.discount]);

  // جديد: جلب الخزن عند فتح الحوار أو إذا لم تُمرر خزن عبر props
 useEffect(() => {
   let mounted = true;
   const load = async () => {
     if (banksList.length > 0) return; // ✅ منع إعادة التحميل
     try {
       const res = await getBanks();
       const list = Array.isArray(res) ? res : (res?.data && Array.isArray(res.data) ? res.data : []);
       if (!mounted) return;
       setBanksList(list.length ? list : (banks || []));
     } catch (err) {
       if (!mounted) return;
       setBanksList(banks || []);
     }
   };
   if (open) load();
   return () => { mounted = false; };
 }, [open, banks]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setForm((f) => ({ ...f, file }));
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // build payload object — makeEmployeeTransaction will convert to FormData
      const payload = {
        date: form.date,
        orginal_amount: form.orginal_amount,
        bouns: form.bouns,
        discount: form.discount,
        total: form.total,
        action_type: form.action_type,
        employee_id: form.employee_id,
        bank_id: form.bank_id,
        note: form.note,
        file: form.file,
      };
      const res = await makeEmployeeTransaction(payload);
      setSnackbar({ open: true, message: "تم سداد للموظف بنجاح", severity: "success" });

      // جديد: أخبر كشف الحساب بتحديث بياناته
      try {
        window.dispatchEvent(new CustomEvent("employeeAccountStatementUpdated", { detail: { employeeId: form.employee_id } }));
      } catch (evtErr) {
        // لا نوقف التدفق لو فشل الإرسال، فقط نسجل
        console.warn("Failed to dispatch employeeAccountStatementUpdated event", evtErr);
      }

      if (typeof onPaid === "function") onPaid(res);
      if (typeof onClose === "function") onClose();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err?.message || "فشل في سداد للموظف", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <>
      <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>سداد للموظف</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">معرف الموظف: {employeeId ?? "-"}</Typography>
          </Box>

          {/* تاريخ العملية */}
          <TextField
            label="التاريخ"
            name="date"
            value={form.date}
            onChange={handleChange}
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          {/* المبلغ الأصلي */}
          <TextField
            label="المبلغ"
            name="orginal_amount"
            value={form.orginal_amount}
            onChange={handleChange}
            fullWidth
            margin="normal"
            type="number"
          />

          {/* removed: الإجمالي - حقل "المدفوع" مخفى ولكن يبقى محسوبًا في state ويُرسل في payload */}

          {/* اختيار الخزنة / البنك */}
          <TextField
            select
            label="الخزنة / البنك"
            name="bank_id"
            value={form.bank_id}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            <MenuItem value="">بدون</MenuItem>
            {loadingBanks ? (
              <MenuItem disabled>جاري التحميل...</MenuItem>
            ) : banksList && banksList.length > 0 ? (
              banksList.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="">لا توجد خزن</MenuItem>
            )}
          </TextField>

          {/* رفع ملف */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <input
              accept="image/*,application/pdf"
              id="salary-file-upload"
              type="file"
              onChange={handleFile}
              style={{ display: "block" }}
            />
          </Box>

          {/* ملاحظة */}
          <TextField
            label="ملاحظة (اختياري)"
            name="note"
            value={form.note}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button onClick={handleSubmit} disabled={submitting || !form.employee_id || !form.orginal_amount} variant="contained">
            {submitting ? <CircularProgress size={20} /> : "سداد"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};


// export default SalaryPayment;
export default SalaryPayment;